/**
 * @file routing.js
 * @description 브라우저 경로 문자열을 페이지 라우트 정보로 매핑하는 유틸리티입니다.
 * @used-in hooks/useAppRouter.js, hooks/useHeaderState.js
 */

import { pageMeta } from '../constants/navigation';

// [비즈니스] 구 편집 모드 URL → 신 관리자 콘솔 탭 매핑입니다.
const LEGACY_EDIT_PATHS = {
  '/company/history_edit': '/admin/history',
  '/news/news_edit': '/admin/news',
  '/cs/faq_edit': '/admin/faq',
};

export function normalizePath(pathname) {
  // 기능 목적: 쿼리/해시를 제거하고 마지막 슬래시를 정리해 비교 기준 경로를 만듭니다.
  if (!pathname) return '/';
  const sanitized = pathname.split('?')[0].split('#')[0];
  return sanitized === '' ? '/' : sanitized.replace(/\/+$/, '') || '/';
}

export function getRoute(pathname) {
  const normalized = normalizePath(pathname);
  // 기능 목적: 뉴스 상세의 slug를 동적 경로에서 먼저 추출합니다.
  const newsMatch = normalized.match(/^\/news\/([^/]+)\.html$/);

  if (normalized === '/') return { page: 'home' };
  if (normalized === '/company/ceo.html') return { page: 'companyCeo', meta: pageMeta.companyCeo };
  if (normalized === '/company/about.html') return { page: 'companyAbout', meta: pageMeta.companyAbout };
  if (normalized === '/company/history.html') return { page: 'companyHistory', meta: pageMeta.companyHistory };
  if (normalized === '/company/customers.html') return { page: 'companyCustomers', meta: pageMeta.companyCustomers };
  if (normalized === '/company/maintenance.html') return { page: 'companyMaintenance', meta: pageMeta.companyMaintenance };
  if (normalized === '/solution/withgmp.html')   return { page: 'solution', meta: pageMeta.solution, activeTab: 'withgmp' };
  if (normalized === '/solution/witheai.html')   return { page: 'solution', meta: pageMeta.solution, activeTab: 'witheai' };
  if (normalized === '/solution/cmms.html')      return { page: 'solution', meta: pageMeta.solution, activeTab: 'cmms' };
  if (normalized === '/solution/fems.html')      return { page: 'solution', meta: pageMeta.solution, activeTab: 'fems' };
  if (normalized === '/solution/withaigmp.html') return { page: 'solution', meta: pageMeta.solution, activeTab: 'withaigmp' };
  if (normalized === '/solution/rws.html') return { page: 'solution', meta: pageMeta.solution, activeTab: 'rws' };
  if (normalized === '/solution/mes.html') return { page: 'solution', meta: pageMeta.solution, activeTab: 'mes' };
  if (normalized === '/solution/wms.html') return { page: 'solution', meta: pageMeta.solution, activeTab: 'wms' };
  // [비즈니스] 구 solution/maintenance URL은 company/maintenance 페이지로 리다이렉트합니다.
  if (normalized === '/solution/maintenance.html') return { page: 'companyMaintenance', meta: pageMeta.companyMaintenance };
  if (normalized === '/news/index.html') return { page: 'newsList', meta: pageMeta.newsList };
  if (newsMatch) return { page: 'newsDetail', meta: pageMeta.newsDetail, slug: newsMatch[1] };
  if (normalized === '/cs/faq.html') return { page: 'faq', meta: pageMeta.faq };
  if (normalized === '/contact' || normalized === '/cs/contact.html' || normalized === '/inquiry') {
    return { page: 'contact', meta: pageMeta.contact };
  }

  // [비즈니스] 인사말·연혁·뉴스·FAQ를 한곳에서 관리하는 관리자 콘솔입니다.
  //            GNB에는 노출하지 않고 URL로만 진입하며, 실제 접근 권한은 서버 세션이 판정합니다.
  // [비즈니스] 콘솔 첫 화면은 인사말 탭입니다(사용자 결정). 연혁은 /admin/history 로 옮겼습니다.
  if (normalized === '/admin') return { page: 'admin', adminTab: 'content', meta: pageMeta.admin };
  if (normalized === '/admin/history') return { page: 'admin', adminTab: 'history', meta: pageMeta.admin };
  if (normalized === '/admin/news') return { page: 'admin', adminTab: 'news', meta: pageMeta.admin };
  if (normalized === '/admin/faq') return { page: 'admin', adminTab: 'faq', meta: pageMeta.admin };
  // [비즈니스] 인사말 탭이 첫 화면이 되기 전에 쓰던 주소입니다. 북마크를 위해 /admin 으로 전환합니다.
  //            replace 전환이라 뒤로가기 루프가 없으며, 일정 기간 뒤 제거해도 무방합니다.
  if (normalized === '/admin/content') return { page: 'redirect', to: '/admin' };

  // [비즈니스] 구 편집 진입 URL(*_edit)을 북마크해 둔 관리자를 위해 콘솔로 보내 줍니다.
  //            일정 기간 뒤 제거해도 무방합니다.
  if (LEGACY_EDIT_PATHS[normalized]) {
    return { page: 'redirect', to: LEGACY_EDIT_PATHS[normalized] };
  }

  return { page: 'notFound', meta: pageMeta.notFound };
}

export function getTopLevelNavKey(pathname) {
  const normalized = normalizePath(pathname);
  // 기능 목적: 상단 GNB 활성 상태를 1차 경로 기준으로 계산합니다.
  if (normalized === '/') return 'home';
  if (normalized.startsWith('/company/')) return 'company';
  if (normalized.startsWith('/solution/')) return 'solution';
  if (normalized.startsWith('/news/')) return 'news';
  if (normalized === '/contact' || normalized === '/inquiry' || normalized.startsWith('/cs/')) return 'cs';
  return '';
}

export function getSubNavKey(pathname) {
  const normalized = normalizePath(pathname);
  // 기능 목적: 하위 메뉴 active 상태를 상세 경로와 1:1로 매핑합니다.
  if (normalized === '/company/about.html') return 'about';
  if (normalized === '/company/ceo.html') return 'ceo';
  if (normalized === '/company/history.html') return 'history';
  if (normalized === '/company/customers.html') return 'customers';
  if (normalized === '/company/maintenance.html') return 'maintenance';
  if (normalized === '/solution/withgmp.html')   return 'withgmp';
  if (normalized === '/solution/witheai.html')   return 'witheai';
  if (normalized === '/solution/cmms.html')      return 'cmms';
  if (normalized === '/solution/fems.html')      return 'fems';
  if (normalized === '/solution/withaigmp.html') return 'withaigmp';
  if (normalized === '/solution/rws.html') return 'rws';
  if (normalized === '/solution/mes.html') return 'mes';
  if (normalized === '/solution/wms.html') return 'wms';
  if (normalized === '/solution/maintenance.html') return 'maintenance';
  if (normalized === '/cs/faq.html') return 'faq';
  if (normalized === '/contact' || normalized === '/cs/contact.html' || normalized === '/inquiry') return 'contact';
  return '';
}
